<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-4">
      <p class="text-body-2 text-medium-emphasis mb-0">
        Showing batches from the last 7 days
      </p>
      <SoButton
        title="Refresh"
        :loading="query.isFetching"
        data-testid="sms-batch-list-refresh"
        @click="query.refetch()"
      />
    </div>

    <div
      v-if="query.isLoading"
      class="d-flex align-center py-6"
      data-testid="sms-batch-list-loading"
    >
      <SoProgress indeterminate :size="24" class="mr-3" />
      <span class="text-body-2">Loading batches...</span>
    </div>

    <SoAlert
      v-else-if="query.isError"
      type="error"
      data-testid="sms-batch-list-error"
    >
      Failed to load SMS batches. Please try refreshing
    </SoAlert>

    <p
      v-else-if="query.data.length === 0"
      class="text-body-2 text-medium-emphasis"
      data-testid="sms-batch-list-empty"
    >
      No batches found in the last 7 days
    </p>

    <SoDataTable
      v-else
      :headers="tableHeaders"
      :items="tableItems"
      :searchable="false"
      :has-pagination="false"
      :compact="true"
      data-testid="sms-batch-list-table"
    >
      <template #item.status="{ internalItem }">
        <SoChip
          :type="statusChipType(internalItem.raw.status)"
          :text="statusLabel(internalItem.raw.status)"
          size="small"
        />
      </template>
    </SoDataTable>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoProgress from "@/components/SoProgress.vue"
  import SoChip from "@/components/SoChip.vue"
  import { SoChipType } from "@/types/components/SoChip"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import {
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import { SmsBatchService } from "@/services/SmsBatchService"
  import { SmsBatchSummaryStatus as BatchStatus } from "@/api/backofficeRest/generated/model"
  import { formatDateTime } from "@/helpers/dateHelper"
  import dayjs from "dayjs"

  export default defineComponent({
    components: {
      SoAlert,
      SoProgress,
      SoChip,
      SoButton,
      SoDataTable,
    },
    data() {
      return {
        query: SmsBatchService.listBatches(),
        SoChipType,
        tableHeaders: [
          { key: "sentAt", title: "Sent at", sortable: false },
          { key: "createdBy", title: "Sent by", sortable: false },
          { key: "status", title: "Status", sortable: false },
          { key: "sentCount", title: "Sent", sortable: false },
          { key: "pendingCount", title: "Pending", sortable: false },
          { key: "failedCount", title: "Failed", sortable: false },
          { key: "totalCount", title: "Total", sortable: false },
        ] as SoDataTableHeader[],
      }
    },
    computed: {
      tableItems(): SoDataTableItem[] {
        return (this.query.data ?? []).map((batch) => ({
          sentAt: this.formatSentAt(batch.createdAt),
          createdBy: batch.createdBy ?? "-",
          status: batch.status,
          sentCount: batch.sentCount ?? "-",
          pendingCount: batch.pendingCount ?? "-",
          failedCount: batch.failedCount ?? "-",
          totalCount: batch.totalCount ?? "-",
        }))
      },
    },
    methods: {
      refetch() {
        this.query.refetch()
      },
      formatSentAt(createdAt: string | null | undefined): string {
        if (!createdAt) return "-"
        return formatDateTime(dayjs(createdAt))
      },
      statusChipType(status: BatchStatus | null | undefined): SoChipType {
        switch (status) {
          case BatchStatus.COMPLETED:
            return SoChipType.SUCCESS
          case BatchStatus.PARTIALLY_COMPLETED:
            return SoChipType.WARNING
          case BatchStatus.FAILED:
            return SoChipType.ERROR
          case BatchStatus.PROCESSING:
            return SoChipType.IMPORTANT
          default:
            return SoChipType.DEFAULT
        }
      },
      statusLabel(status: BatchStatus | null | undefined): string {
        switch (status) {
          case BatchStatus.COMPLETED:
            return "Completed"
          case BatchStatus.PARTIALLY_COMPLETED:
            return "Partial"
          case BatchStatus.FAILED:
            return "Failed"
          case BatchStatus.PROCESSING:
            return "Processing"
          case BatchStatus.PENDING:
            return "Pending"
          default:
            return status ?? "-"
        }
      },
    },
  })
</script>
